import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { NumbersInputOnlyDirective } from '../../../../core/directives/numbers-input-only.directive';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { LastLoggedEmployee } from '../../../../core/types';
import { AvatarComponent } from '../../../../shared/components/avatar/avatar.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { FormControlComponent } from '../../../../shared/components/form-control/form-control.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartService } from '../../core/services/CartService';
import { IDiscountPercentageRange } from '../../core/interfaces/IDiscountPercentageRange';

type DiscountMode = 'normal' | 'authorized';
type AuthorizationStep = 'authorization' | 'percentage';

export interface IFixedDiscountModalData {
  mode: DiscountMode;
  range?: IDiscountPercentageRange;
}

/**
 * Fixed discount entry sharing one on-screen percentage numpad across two modes:
 * - 'normal': single percentage step constrained to a configured [min, max] range,
 * - 'authorized': supervisor identity step (employee + email + PIN) followed by the
 *   same percentage step, validated server-side against the supervisor's discount range.
 * Percentages use a cent-based model (e.g. "550" => 5.50%) so fractional discounts work.
 * Mirrors legacy CXCash DlgRebate behavior without replacing the operator session.
 */
@Component({
  selector: 'app-fixed-discount-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    FormsModule,
    FormControlComponent,
    ContentBoxComponent,
    ContentEditableInputComponent,
    KeyboardComponent,
    AvatarComponent,
    NumbersInputOnlyDirective
  ],
  templateUrl: './fixed-discount-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FixedDiscountModalComponent extends BaseModalComponent<IFixedDiscountModalData> {

  @ViewChild(ContentEditableInputComponent) contentEditableInputComponent: ContentEditableInputComponent;
  @Output() discountApplied = new EventEmitter<void>();

  public mode: DiscountMode = 'normal';
  public range: IDiscountPercentageRange | null = null;
  public email: string;
  public pin: string;
  public percentageValue: string;
  public selectedEmployee: LastLoggedEmployee;
  public lastLoggedEmployees: LastLoggedEmployee[] = [];
  public readonly step = signal<AuthorizationStep>('authorization');
  public readonly isLoading = signal(false);

  private _isPercentageValueSet = false;
  private readonly _cartService = inject(CartService);

  public override open(val: IFixedDiscountModalData): void {
    this.mode = val.mode;
    this.range = val.range ?? null;
    this.email = null;
    this.pin = null;
    this.percentageValue = null;
    this.selectedEmployee = null;
    this.lastLoggedEmployees = [];
    this._isPercentageValueSet = false;
    this.isLoading.set(false);
    this.step.set(this.mode === 'authorized' ? 'authorization' : 'percentage');

    super.open(val);

    if (this.mode === 'authorized') {
      this.lastLoggedEmployees = this.employeesStore.lastLoggedEmployees();

      if (this.lastLoggedEmployees.length > 0) {
        this.selectEmployee(this.lastLoggedEmployees[0]);
      }
    } else if (this.range) {
      this.updatePercentageValue((this.range.min * 100).toString());
      this._isPercentageValueSet = true;
    }
  }

  public get isNextDisabled(): boolean {
    return !this.email || !this.pin;
  }

  public get isConfirmDisabled(): boolean {
    if (this.isLoading()) {
      return true;
    }

    const percentage = Number(this.percentageValue) / 100;

    if (this.mode === 'normal') {
      return isNaN(percentage) || percentage < this.range?.min || percentage > this.range?.max;
    }

    return !this.percentageValue || isNaN(percentage) || percentage <= 0 || percentage > 100;
  }

  public selectEmployee(employee: LastLoggedEmployee): void {
    this.selectedEmployee = employee;
    this.email = employee.email;
    this.pin = null;
    this.cdr.detectChanges();
  }

  public startNewEmployee(): void {
    this.selectedEmployee = null;
    this.email = null;
    this.pin = null;
    this.cdr.detectChanges();
  }

  public goToPercentageStep(): void {
    if (this.isNextDisabled) {
      return;
    }

    this.percentageValue = null;
    this._isPercentageValueSet = false;
    this.step.set('percentage');
    this.cdr.detectChanges();
  }

  public goToAuthorizationStep(): void {
    this.step.set('authorization');
    this.cdr.detectChanges();
  }

  public handlePinDeleteBack(): void {
    this.pin = this.pin ? this.pin.substring(0, this.pin.length - 1) : '';
    this.cdr.detectChanges();
  }

  public handlePinKeyClick(val: string): void {
    this.pin = this.pin ? `${this.pin}${val}` : val;
    this.cdr.detectChanges();
  }

  public handlePercentageClear(): void {
    this._isPercentageValueSet = false;
    this.updatePercentageValue('');
  }

  public handlePercentageDeleteBack(): void {
    this._isPercentageValueSet = false;
    this.updatePercentageValue(this.percentageValue ? this.percentageValue.substring(0, this.percentageValue.length - 1) : '');
  }

  public handlePercentageKeyClick(val: string): void {
    if (this._isPercentageValueSet) {
      this._isPercentageValueSet = false;
      this.percentageValue = '';
    }

    this.updatePercentageValue(this.percentageValue ? `${this.percentageValue}${val}` : val);
  }

  public confirm(): void {
    if (this.isConfirmDisabled) {
      return;
    }

    this.isLoading.set(true);

    const discountPercentage = Number(this.percentageValue) / 100;

    const request$ = this.mode === 'authorized'
      ? this._cartService.applyFixedDiscountWithAuthorizationToCart(this.email, +this.pin, discountPercentage)
      : this._cartService.applyFixedDiscountToCart(discountPercentage);

    request$
      .pipe(
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: () => {
          this.discountApplied.emit();
          this.close();
        },
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`apply-fixed-discount/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private updatePercentageValue(val: string): void {
    this.percentageValue = val;

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = this.percentageValue;
    }

    this.cdr.detectChanges();
  }
}
